import { yupResolver } from '@hookform/resolvers/yup';
import { memo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import StartConflictModal from '@/components/StartConflictModal';
import FieldError from '@/forms/FieldError';
import { useConflictGatedSubmit } from '@/hooks/useConflictGatedSubmit';
import {
  useCreateMedicalStarterSequence,
  useMedicalStarterStartNodes
} from '@/hooks/useScheduleExpectations/useScheduleExpectations';
import ScheduleExpectationRepository from '@/repositories/ScheduleExpectationRepository';
import { Button, InputGroup, MondayPicker } from '@/ui';
import Spinner from '@/ui/components/Spinner';
import { getClosestUpcomingMonday, todayLocalDateString } from '@/utils/dates';

import type { Props } from './types';

const MedicalStarterSequenceForm = ({ onSuccess, phaseId, timeZone }: Props) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue
  } = useForm<{ start_at: string }>({
    defaultValues: {
      start_at: todayLocalDateString()
    },
    resolver: yupResolver(
      yup.object({
        start_at: yup.string().label('Start date').required('Start date is a required field')
      })
    )
  });

  const createMutation = useCreateMedicalStarterSequence(phaseId);
  const { data: startNodes, isPending: isPendingStartNodes } = useMedicalStarterStartNodes({
    phaseId
  });
  const repository = ScheduleExpectationRepository();
  const { pendingConflicts, guardSubmit, confirmPending, clearPending } =
    useConflictGatedSubmit<string>();

  // Default to the closest upcoming Monday, unless that's before the earliest
  // allowed start date -- then use the earliest allowed one instead.
  useEffect(() => {
    if (startNodes?.min_date) {
      const minDate = startNodes.min_date.split('T')[0];
      const closestMonday = getClosestUpcomingMonday(new Date(), timeZone);
      setValue('start_at', closestMonday >= minDate ? closestMonday : minDate);
    }
  }, [startNodes?.min_date, setValue, timeZone]);

  const doSubmit = (startAt: string) => {
    createMutation.mutate(
      { data: { phase: phaseId, start_at: startAt } },
      { onSuccess: () => onSuccess?.() }
    );
  };

  const submit = async (formValues: { start_at: string }) => {
    await guardSubmit(
      formValues.start_at,
      () => repository.getMedicalStarterStartConflict({ phaseId, startAt: formValues.start_at }),
      doSubmit
    );
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <InputGroup>
        <FieldError message={errors.start_at?.message} />
        <Controller
          control={control}
          name="start_at"
          render={({ field }) => {
            if (isPendingStartNodes) {
              return <Spinner />;
            }

            return (
              <MondayPicker
                label="Start Date"
                minDate={startNodes?.min_date ? new Date(startNodes.min_date) : undefined}
                name="start_at"
                onChange={field.onChange}
                preventOpenOnFocus={true}
                value={field.value}
                withPortal={true}
              />
            );
          }}
        />
      </InputGroup>

      <div className="mt-4 flex justify-end gap-3">
        <Button variant="outline" color="neutral" onClick={onSuccess} type="button">
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={createMutation.isPending}
          isLoading={createMutation.isPending}
          type="submit">
          Create
        </Button>
      </div>

      <StartConflictModal
        conflicts={pendingConflicts}
        timeZone={timeZone}
        onContinue={() => confirmPending(doSubmit)}
        onCancel={clearPending}
      />
    </form>
  );
};

export default memo(MedicalStarterSequenceForm);
